import React, { useState, useEffect } from 'react';
import { useMap } from 'react-leaflet';
import { 
  Select, 
  MenuItem, 
  FormControl,
  Paper,
  Typography,
  Box
} from '@mui/material';

const MapSpeciesFilter = ({ speciesData, onFilterChange }) => {
  const [selectedSpecies, setSelectedSpecies] = useState('all');
  const map = useMap();

  // Sort species alphabetically by local name (the key)
  const sortedSpeciesEntries = Object.entries(speciesData).sort(
    ([localNameA], [localNameB]) => localNameA.localeCompare(localNameB)
  );

  // Disable map interaction when the select is open
  useEffect(() => {
    const selectContainer = document.querySelector('.species-select-container');
    if (selectContainer) {
      selectContainer.addEventListener('mouseenter', () => {
        if (map) {
          map.dragging.disable();
          map.scrollWheelZoom.disable();
        }
      });
      
      selectContainer.addEventListener('mouseleave', () => {
        if (map) {
          map.dragging.enable();
          map.scrollWheelZoom.enable();
        }
      });
    }

    return () => {
      if (map) {
        map.dragging.enable();
        map.scrollWheelZoom.enable();
      }
    };
  }, [map]);

  const handleSpeciesChange = (event) => {
    const value = event.target.value;
    setSelectedSpecies(value);
    onFilterChange(value);
  };

  // Used by both the Select and the clickable legend rows
  const applySpeciesFilter = (value) => {
    setSelectedSpecies(value);
    onFilterChange(value);
  };

  // Stop event propagation to prevent map interaction
  const handleSelectClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <Box 
      onClick={handleSelectClick}
      sx={{
        position: 'absolute',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        maxHeight: '60vh',
        maxWidth: '20%',
        width: '20%',
      }}
    >
      <Paper 
        elevation={3}
        sx={{
          p: 1,
          overflow: 'hidden',
          background: 'linear-gradient(to bottom, #fafafa, #f5f5f5)',
          border: '1px solid #e0e0e0',
        }}
      >
        <FormControl 
          fullWidth 
          sx={{ 
            mb: 2,
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#7EA10D',
              '&:hover': {
                backgroundColor: '#8FBF26'
              },
              '& .MuiSelect-select': {
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }
            }
          }}
        >
          <Select
            value={selectedSpecies}
            onChange={handleSpeciesChange}
            displayEmpty
            sx={{ minWidth: 0, width: '100%' }}
            size='small'
            renderValue={(selected) => (selected === "all" ? "Diversity Filter" : selected)}
          >
            <MenuItem 
              value="all"
              sx={{
                backgroundColor: selectedSpecies === 'all' ? '#7EA10D' : 'transparent',
                fontWeight: selectedSpecies === 'all' ? 700 : 400,
                fontSize: '0.7rem',
                '&:hover': { backgroundColor: '#8FBF26' },
                '&.Mui-selected': { backgroundColor: '#7EA10D' },
                '&.Mui-selected:hover': { backgroundColor: '#8FBF26' }
              }}
            >
              All Species
            </MenuItem>
            {sortedSpeciesEntries.map(([species, data]) => (
              <MenuItem 
                key={species} 
                value={species}
                sx={{
                  backgroundColor: selectedSpecies === species ? '#7EA10D' : 'transparent',
                  '&:hover': {
                    backgroundColor: '#8FBF26'
                  },
                  '&.Mui-selected': {
                    backgroundColor: '#7EA10D'
                  },
                  '&.Mui-selected:hover': {
                    backgroundColor: '#8FBF26'
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <img 
                    src={data.icon || '/images/icon/leaf.png'} 
                    alt={species}
                    style={{ width: 20, height: 20, objectFit: 'contain' }}
                  />
                   <span style={{
                     color: '#000000',
                     fontSize:'0.7rem',
                     fontWeight: selectedSpecies === species ? 700 : 400
                   }}>{species} ({data.count})</span>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box className="legend">
          <Box 
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              maxHeight: 'calc(60vh - 100px)',
              overflowY: 'auto',
              pr: 1
            }}
          >
             {sortedSpeciesEntries.map(([species, data]) => (
              <Box 
                key={species}
                onClick={() => applySpeciesFilter(species)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  borderRadius: 1,
                  cursor: 'pointer',
                  backgroundColor: selectedSpecies === species ? '#EAF3D0' : '#f5f5f5',
                  border: selectedSpecies === species ? '2px solid #7EA10D' : '2px solid transparent',
                  '&:hover': {
                    backgroundColor: '#e3f2fd'
                  },
                  transition: 'background-color 0.2s, border-color 0.2s',
                }}
              >
                <Box 
                  sx={{
                    width: 32,
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'white',
                    borderRadius: '50%',
                    boxShadow: 1,
                    p: 0.5,
                    marginRight: 0.5,
                    marginLeft: 0.5,
                    flexShrink: 0
                  }}
                >
                  <img 
                    src={data.icon || '/images/icon/leaf.png'}
                    alt={species}
                    style={{ minWidth: 22, minHeight: 22, objectFit: 'contain'}}
                  />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0, minWidth: 0, py: 0.5 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: selectedSpecies === species ? 700 : 600,
                      color: selectedSpecies === species ? '#557A05' : 'inherit',
                      lineHeight: 1.2,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      fontSize:'0.7rem'
                    }}
                  >
                    {species} ({data.count})
                  </Typography>
                  {data.scientificName && (
                    <Typography
                      variant="caption"
                      sx={{
                        fontStyle: 'italic',
                        color: 'text.secondary',
                        lineHeight: 1.2,
                        fontSize: '0.7rem',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {data.scientificName}
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default MapSpeciesFilter;